"use client"

import * as React from "react"

import { TransactionAmount } from "@/features/finance/components/transactions/transaction-direction-sign"
import {
  cn,
  SectionCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@ibada/ui"
import { DataTableAutomaticColumnHeader } from "@ibada/ui/components/data-table"
import { useLocale, useTranslations } from "@/i18n"
import { OverviewTableColumnType, type OverviewResponse, type OverviewTable } from "@ibada/shared-types/overview"
import { coerceNumber, readMoney } from "@/shared/lib/overview/overview-formatting"
import { createCurrencyFormatter } from "@/shared/lib/formatting/currency"
import { formatDate } from "@/features/finance/lib/finance-utils"

const TABLE_PRIORITY = [
  "recentTransactions",
  "activeActivities",
  "recentMembers",
  "recentActivities",
  "upcomingActivities",
  "recentAssets",
  "assetsNeedingAttention",
] as const

export type DashboardRecentRecordKind = "transactions" | "financeActivities" | "members" | "activities" | "assets"

export type RecentRecordItem = {
  id: string
  title: string
  detail: string
  meta: string
  badge: string | null
  tone: "income" | "expense" | "neutral"
  transactionType?: unknown
}

type DashboardRecentRecordsCardProps = {
  overview: OverviewResponse | null
  currencyCode: string
  placement?: "standard" | "topRail" | "tallRail"
}

export function getDashboardRecentRecordKind(tableId: string): DashboardRecentRecordKind {
  if (tableId === "recentTransactions") return "transactions"
  if (tableId === "activeActivities") return "financeActivities"
  if (tableId === "recentMembers") return "members"
  if (tableId === "recentActivities" || tableId === "upcomingActivities") return "activities"
  return "assets"
}

/**
 * What a recent transaction row is called, most specific first. The overview API sends the account as
 * `category` / `accountingAccountName`; the older `incomeCategoryName` / `expenseCategoryName` keys are kept for
 * payloads that still carry them, then the activity and the description.
 */
export const RECENT_TRANSACTION_TITLE_KEYS = [
  "category",
  "accountingAccountName",
  "incomeCategoryName",
  "expenseCategoryName",
  "activityTitle",
  "description",
]

function getString(row: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key]
    if (typeof value === "string" && value.trim().length > 0) return value.trim()
  }
  return null
}

function formatValue(
  raw: unknown,
  columnType: OverviewTableColumnType,
  currencyCode: string,
  locale: string,
): string {
  if (raw === null || raw === undefined || raw === "") return "—"

  if (columnType === OverviewTableColumnType.Date) {
    return formatDate(String(raw)) ?? String(raw)
  }

  if (columnType === OverviewTableColumnType.Money) {
    // The cell is a canonical decimal string (IBA-116). It goes to `Intl` untouched — `Number(raw)` here was a
    // lossy step taken on the way to a formatter that parses the string exactly.
    const amount = readMoney(raw)
    if (amount === null) return String(raw)
    return createCurrencyFormatter(currencyCode, locale).format(amount)
  }

  if (columnType === OverviewTableColumnType.Number) {
    const value = coerceNumber(raw)
    if (value === null) return String(raw)
    return value.toLocaleString(locale, { maximumFractionDigits: 2 })
  }

  return String(raw)
}

function formatColumn(
  table: OverviewTable,
  row: Record<string, unknown>,
  columnKey: string,
  currencyCode: string,
  locale: string,
): string {
  const column = table.columns.find((item) => item.key === columnKey)
  if (!column) return "—"
  return formatValue(row[column.key], column.columnType, currencyCode, locale)
}

export function findDashboardRecentRecordsTable(overview: OverviewResponse | null): OverviewTable | null {
  if (!overview) return null

  for (const tableId of TABLE_PRIORITY) {
    const table = overview.tables.find((item) => item.id === tableId)
    if (table && table.rows.length > 0) return table
  }

  return null
}

export function hasDashboardRecentRecords(overview: OverviewResponse | null): boolean {
  return Boolean(findDashboardRecentRecordsTable(overview))
}

export function buildRecordItem(
  table: OverviewTable,
  row: Record<string, unknown>,
  rowIndex: number,
  currencyCode: string,
  locale: string,
): RecentRecordItem {
  const kind = getDashboardRecentRecordKind(table.id)
  const id = getString(row, ["id"]) ?? `${table.id}-${rowIndex}`

  if (kind === "transactions") {
    const amount = formatColumn(table, row, "amount", currencyCode, locale)
    const category = getString(row, RECENT_TRANSACTION_TITLE_KEYS) ?? "—"
    // The description is the second line when it says something the title does not.
    const description = getString(row, ["description"])
    const date = formatColumn(table, row, "occurredAt", currencyCode, locale)

    return {
      id,
      title: category,
      detail: amount,
      meta: description && description !== category ? `${date} · ${description}` : date,
      badge: null,
      tone: "neutral",
      transactionType: row.type,
    }
  }

  if (kind === "members") {
    const firstName = getString(row, ["firstName"]) ?? ""
    const lastName = getString(row, ["lastName"]) ?? ""
    const name = `${firstName} ${lastName}`.trim()
    return {
      id,
      title: name || getString(row, ["email", "id"]) || "—",
      // Middle column = the branch the member belongs to; last column = the date they were added.
      detail: getString(row, ["branchName"]) ?? "—",
      meta: formatColumn(table, row, "createdAt", currencyCode, locale),
      badge: formatColumn(table, row, "status", currencyCode, locale),
      tone: "neutral",
    }
  }

  if (kind === "activities" || kind === "financeActivities") {
    const startsAt = formatColumn(table, row, "startsAt", currencyCode, locale)
    return {
      id,
      title: getString(row, ["title", "name"]) ?? "—",
      detail: getString(row, ["responsibleMemberName"]) ?? startsAt,
      meta: startsAt,
      badge: getString(row, ["status"]) ?? null,
      tone: "neutral",
    }
  }

  return {
    id,
    title: getString(row, ["name", "title", "assetName"]) ?? "—",
    detail: getString(row, ["category", "categoryName", "condition"]) ?? "—",
    meta: formatColumn(table, row, "updatedAt", currencyCode, locale),
    badge: getString(row, ["status"]) ?? null,
    tone: "neutral",
  }
}

export function DashboardRecentRecordsCard({
  overview,
  currencyCode,
  placement = "standard",
}: DashboardRecentRecordsCardProps) {
  const locale = useLocale()
  const t = useTranslations("dashboard.admin.recentRecords")
  const table = React.useMemo(() => findDashboardRecentRecordsTable(overview), [overview])
  const kind = table ? getDashboardRecentRecordKind(table.id) : null

  const rows = React.useMemo(() => {
    if (!table) return []
    return table.rows
      .slice(0, 5)
      .map((row, index) => buildRecordItem(table, row, index, currencyCode, locale))
  }, [currencyCode, locale, table])

  if (!table || !kind || rows.length === 0) return null

  const shellClassName =
    placement === "tallRail"
      ? "flex h-full min-h-[30rem] flex-col overflow-hidden p-4"
      : placement === "topRail"
        ? "flex h-[18rem] flex-col overflow-hidden p-4"
        : "flex h-[24rem] flex-col overflow-hidden p-4"

  return (
    <SectionCard
      variant="surface"
      className={cn(shellClassName, "min-w-0 @container/recent")}
      title={t(`title.${kind}`)}
      description={t(`description.${kind}`)}
      headerClassName='min-w-0 space-y-1 [&_[data-slot=card-title]]:truncate [&_[data-slot=card-title]]:text-base [&_[data-slot=card-title]]:font-semibold [&_[data-slot=card-description]]:truncate'
      contentClassName='mt-2 min-h-0 flex-1 overflow-hidden pt-0'
    >
      <div className='min-h-0 w-full min-w-0 flex-1 overflow-hidden'>
        {/* Single table layout for every viewport. The wrapper provides
            horizontal scroll on narrow screens so the tabular structure
            is preserved instead of degrading to a card list. */}
        <div className='h-full w-full min-w-0 overflow-auto'>
          <Table className='table-auto'>
            <TableHeader>
              <TableRow>
                {kind === "transactions" ?
                  <>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="amount" label={t("columns.amount")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="category" label={t("columns.category")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="date" label={t("columns.date")} /></TableHead>
                  </>
                : kind === "members" ?
                  <>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="record" label={t("columns.record")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="branch" label={t("columns.branch")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="date" label={t("columns.date")} /></TableHead>
                  </>
                : <>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="record" label={t("columns.record")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="detail" label={t("columns.detail")} /></TableHead>
                    <TableHead className='h-8 py-1 text-left text-xs'><DataTableAutomaticColumnHeader columnId="date" label={t("columns.date")} /></TableHead>
                  </>
                }
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  {kind === "transactions" ?
                    <>
                      <TableCell className='max-w-[10rem] truncate py-2 text-left text-sm'>
                        <TransactionAmount type={row.transactionType} amountLabel={row.detail} showSign />
                      </TableCell>
                      <TableCell className='max-w-[12rem] py-2'>
                        <span className='block truncate text-sm font-medium'>{row.title}</span>
                      </TableCell>
                      <TableCell className='whitespace-nowrap py-2 text-sm text-muted-foreground'>{row.meta}</TableCell>
                    </>
                  : <>
                      <TableCell className='max-w-[12rem] py-2'>
                        <span className='block truncate text-sm font-medium'>{row.title}</span>
                      </TableCell>
                      <TableCell className='max-w-[10rem] truncate py-2 text-sm text-muted-foreground'>{row.detail}</TableCell>
                      <TableCell className='whitespace-nowrap py-2 text-sm text-muted-foreground'>{row.meta}</TableCell>
                    </>
                  }
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </SectionCard>
  )
}
